import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, Download, FileText, Phone, Mail, User, Stethoscope, Activity, Calendar } from 'lucide-react';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';




const OTBookingTable = () => {
  const navigate = useNavigate();
  const [otBookings, setOtBookings] = useState([]);
  
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Date filtering states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
 useEffect(() => {
    const fetchOTBookings = async () => {
      setLoading(true);
      setError('');
      try {
  const response = await axios.get(`${API_BASE_URL}/get_ot_bookings`,{ withCredentials: true } );
        const data = response.data?.ot_bookings;
        if (Array.isArray(data)) {
          setOtBookings(data);
          setFilteredRows(data);
        } else {
          setError('Invalid data from server');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to fetch OT bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchOTBookings();
  }, [startDate, endDate]);

  // Delete confirmation modal handlers (moved outside useEffect)
  const handleDeleteConfirm = (uhid_no) => {
    // Find the booking to delete and open the modal
    const booking = otBookings.find(b => b.uhid_no === uhid_no);
    if (booking) {
      setDeleteConfirm(booking);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const uhid_no = deleteConfirm.uhid_no;
    try {
      const resp = await axios.delete(`${API_BASE_URL}/delete_ot_booking_by_uhid/${uhid_no}`);
      if (resp.status === 200) {
        setOtBookings(prev => prev.filter(b => b.uhid_no !== uhid_no));
        setFilteredRows(prev => prev.filter(b => b.uhid_no !== uhid_no));
        setSuccessMessage('Booking deleted');
      } else {
        setError('Failed to delete');
      }
    } catch (err) {
      console.error(err);
      setError('Error deleting booking');
    } finally {
      setDeleteConfirm(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };
  // Filter data based on search term and date range
  useEffect(() => {
    let filtered = otBookings;

if (searchTerm) {
  const lowerSearch = searchTerm.toLowerCase();
  filtered = filtered.filter(booking =>
    (booking.patient_name || '').toString().toLowerCase().includes(lowerSearch) ||
    (booking.mobile || '').toString().includes(searchTerm) ||
    (booking.uhid_no || '').toString().toLowerCase().includes(lowerSearch) ||
    (booking.email || '').toString().toLowerCase().includes(lowerSearch) ||
    (booking.type_of_surgery || '').toString().toLowerCase().includes(lowerSearch) ||
    (booking.surgical_code || '').toString().toLowerCase().includes(lowerSearch) ||
    (booking.priority_status || '').toString().toLowerCase().includes(lowerSearch) ||
    (booking.room_no || '').toString().toLowerCase().includes(lowerSearch)
  );
}


    // Date range filter
    if (startDate && endDate) {
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.booking_datetime);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Include entire end date
        return bookingDate >= start && bookingDate <= end;
      });
    }

    setFilteredRows(filtered);
    setCurrentPage(0);
  }, [searchTerm, startDate, endDate, otBookings]);

  // Validate date range
  const validateDateRange = (start, end) => {
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      
      if (startDate > endDate) {
        return 'Start date cannot be later than end date';
      }
    }
    return '';
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    const validationError = validateDateRange(newStartDate, endDate);
    setDateError(validationError);
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    const validationError = validateDateRange(startDate, newEndDate);
    setDateError(validationError);
  };

  const handleDateFilter = () => {
    if (dateError) {
      setError('Please fix date range errors before filtering');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setError('');
    setSuccessMessage(`Filtered ${filteredRows.length} records for date range`);
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setDateError('');
    setError('');
    setSuccessMessage('');
    setFilteredRows(otBookings);
    setCurrentPage(0);
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'emergency':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'urgent':
        return 'bg-orange-100 text-orange-800 border border-orange-200';
      case 'routine':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'elective':
        return 'bg-green-100 text-green-800 border border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Paginated data
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const paginatedBookings = filteredRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );
  const handleEdit = (uhid_no) => {
    if (uhid_no) {
      navigate(`/editOtBooking/${uhid_no}`);
    } else {
      setError('Invalid UHID number');
    }
  };


  const handleAddBooking = () => {
    navigate('/addOTBooking');
  };


  const handleExportPDF = async () => {
    console.log('Exporting PDF...');
    // setSuccessMessage('PDF export functionality would be implemented here');
    const doc = new jsPDF();
 const logoUrl = '/logo.png';
   const toDataURL = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
   try {
    const logoData = await toDataURL(logoUrl);
    doc.addImage(logoData, 'PNG', 14, 10, 30, 30); // (image, format, x, y, width, height)
  } catch (error) {
    console.error('Failed to load logo image', error);
  }
  doc.setFontSize(16);
  doc.text("OT Booking List", 50, 25);

  const tableColumn = [
    "S/No",
    "UHID No",
    "Patient Name",
    "Booking Date & Time",
    "Surgery Type",
    "Surgical Code",
    "Priority",
    "Room No",
  ];

  const tableRows = filteredRows.map((row, index) => [
    index + 1,
    row.uhid_no,
    row.patient_name,
    new Date(row.booking_datetime).toLocaleString(),
    row.type_of_surgery,
    row.surgical_code,
    row.priority_status,
    row.room_no || "N/A",
  ]);

  autoTable(doc, {
      startY: 45,
      head: [tableColumn],
      body: tableRows,
       headStyles: {
      fillColor: [0, 128, 128], // Teal color
      textColor: [255, 255, 255],
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240],
    },
    });
  

  doc.save("OT_Bookings.pdf");
  };

  const resetSearch = () => {
    setSearchTerm('');
    setCurrentPage(0);
  };

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-12 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
   

{/* Filters and Search */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-6 flex-wrap">
    
    {/* Search */}
    <div className="relative w-full lg:flex-1">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
      <input
        type="text"
        placeholder="Search by patient name, UHID, surgery type, room, etc..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(0);
        }}
        className="w-full pl-11 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
      />
      {searchTerm && (
        <button
          onClick={resetSearch}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      )}
    </div>

   <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
  {/* Start Date */}
  <div className="flex-1">
    <label className="block mb-1 text-sm font-medium text-gray-700">Start Date</label>
    <input
      type="date"
      value={startDate}
      min={new Date().toISOString().split("T")[0]}
      onChange={handleStartDateChange}
      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
    />
  </div>

  {/* End Date */}
  <div className="flex-1">
    <label className="block mb-1 text-sm font-medium text-gray-700">End Date</label>
    <input
      type="date"
      value={endDate}
       min={startDate || new Date().toISOString().split("T")[0]} 
      onChange={handleEndDateChange}
      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
    />
  </div>
</div>


    <div className="flex gap-3 w-full sm:w-auto flex-wrap">
      <button 
        onClick={handleDateFilter}
        className="w-full sm:w-auto px-4 py-2 border border-gray-200 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        <Calendar className="h-5 w-5" />
        Filter by Date
      </button>

      <button
        onClick={handleClearFilters}
        className="w-full sm:w-auto px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Clear Filters
      </button>
    </div>
  </div>

  {/* Date Error */}
  {dateError && (
    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
      <p className="text-red-600 text-sm">{dateError}</p>
    </div>
  )}
</div>

{/* Action Bar */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
      <button
        onClick={handleAddBooking}
        className="flex items-center justify-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium w-full sm:w-auto"
      >
        <Plus className="h-4 w-4" />
        Add OT Booking
      </button>

      <button
        onClick={handleExportPDF}
        className="flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium w-full sm:w-auto"
      >
        <Download className="h-4 w-4" />
        Export PDF
      </button>
    </div>
  </div>
</div>


      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Results Summary */}
      {(searchTerm || startDate || endDate) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">
            Found {filteredRows.length} booking{filteredRows.length !== 1 ? 's' : ''} 
            {searchTerm && ` matching "${searchTerm}"`}
            {startDate && endDate && ` between ${startDate} and ${endDate}`}
          </p>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teal-50 border-b border-teal-100">
                <th className="text-left p-4 font-semibold text-teal-900">UHID</th>
                <th className="text-left p-4 font-semibold text-teal-900">Patient Name</th>
                <th className="text-left p-4 font-semibold text-teal-900">Room No</th>
                <th className="text-left p-4 font-semibold text-teal-900">Date</th>
                <th className="text-left p-4 font-semibold text-teal-900">Time</th>
                <th className="text-left p-4 font-semibold text-teal-900">Surgery Type</th>
                <th className="text-left p-4 font-semibold text-teal-900">Surgical Code</th>
                <th className="text-left p-4 font-semibold text-teal-900">Priority</th>
                <th className="text-center p-4 font-semibold text-teal-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBookings.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-12 text-center">
                    <div className="flex flex-col items-center">
                      <Search className="h-16 w-16 text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-500 mb-2">No bookings found</h3>
                      <p className="text-gray-400 mb-4">Try adjusting your search criteria</p>
                      {(searchTerm || startDate || endDate) && (
                        <button
                          onClick={handleClearFilters}
                          className="text-teal-600 hover:text-teal-700 font-medium"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedBookings.map((booking, index) => {
                  const { date, time } = formatDateTime(booking.booking_datetime);
                  return (
                    <tr 
                      key={booking.uhid_no} 
                      className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}
                    >
                      <td className="p-4">
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono text-sm">
                          {booking.uhid_no}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-gray-900">{booking.patient_name}</div>
                        <div className="text-sm text-gray-500">{booking.mobile}</div>
                      </td>
                      <td className="p-4">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          {booking.room_no}
                        </span>
                      </td>
                      <td className="p-4 text-gray-900">{date}</td>
                      <td className="p-4 text-gray-900">{time}</td>
                      <td className="p-4">
                        <div className="text-gray-900">{booking.type_of_surgery}</div>
                      </td>
                      <td className="p-4">
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-mono">
                          {booking.surgical_code}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-sm font-medium ${getPriorityColor(booking.priority_status)}`}>
                          {booking.priority_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => handleEdit(booking.uhid_no)} 
                            className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                            title="Edit Booking"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                        
                          <button 
                            onClick={() => handleDeleteConfirm (booking.uhid_no)} 
                            className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
                            title="Delete Booking"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginatedBookings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Search className="h-16 w-16 text-gray-300 mb-4 mx-auto" />
            <h3 className="text-lg font-medium text-gray-500 mb-2">No bookings found</h3>
            <p className="text-gray-400 mb-4">Try adjusting your search criteria</p>
            {(searchTerm || startDate || endDate) && (
              <button
                onClick={handleClearFilters}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          paginatedBookings.map((booking) => {
            const { date, time } = formatDateTime(booking.booking_datetime);
            return (
              <div key={booking.uhid_no} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg mb-1">{booking.patient_name}</h3>
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono text-sm">
                      {booking.uhid_no}
                    </span>
                  </div>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${getPriorityColor(booking.priority_status)}`}>
                    {booking.priority_status}
                  </span>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3">
                    <Activity className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{booking.type_of_surgery}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{booking.surgical_code}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{date} at {time}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-4 h-4 bg-blue-100 rounded flex items-center justify-center text-blue-800 text-xs font-bold">R</span>
                    <span className="text-gray-600">{booking.room_no}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button 
                    onClick={() => handleEdit(booking.uhid_no)} 
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors text-sm font-medium" 
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                   
                  <button 
                    onClick={() => handleDeleteConfirm (booking.uhid_no)} 
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors text-sm font-medium" 
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {filteredRows.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, filteredRows.length)} of {filteredRows.length} bookings
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(0)}
                disabled={currentPage === 0}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">
                {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete OT Booking</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the OT booking for <strong>{deleteConfirm.patient_name}</strong> (UHID: {deleteConfirm.uhid_no})? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OTBookingTable;
